// @avantia common-code

import { MockPolicyKey, namedPolicies } from './mockPolicies';
import { CustomerDetails, PerilType, Policy, PolicyMap } from './types/policyTypes';

export {
  endorsements,
  loremIpsum,
  mockAddons,
  mockAddonDetails,
  mockAvailableAddons,
  mockExcesses,
  mockPolicyEndDate,
  mockPolicyStartDate,
  mockSpecifiedItems
} from './mockPolicies/shared';
import {
  createMockPolicy,
  mockAddonDetails,
  mockPolicyStartDate,
  mockPolicyEndDate,
  mockSpecifiedItems
} from './mockPolicies/shared';

export type { MockPolicyKey };

export const singleModePolicyInstance = 'B12/4639145';

// Buildings, Florence, AD=Basic — used as the default in single-policy mode and in reducer tests.
export const singleModePolicy: Policy = createMockPolicy({
  policyNumber: singleModePolicyInstance,
  policyDetailsId: '123441237781',
  coverType: 'Buildings',
  productDescription: 'Florence',
  inceptionDate: mockPolicyStartDate,
  startDate: mockPolicyStartDate,
  endDate: mockPolicyEndDate,
  accidentalDamageLevel: { Buildings: 'Basic', Contents: 'None' },
  specifiedItems: mockSpecifiedItems,
  addonDetails: mockAddonDetails,
  additionalPolicyHolders: [
    { title: 'Mr', dateOfBirth: '1964-04-16T00:00:00', forename: 'test', surname: 'nhione' },
    { title: 'Mr', dateOfBirth: '1964-04-16T00:00:00', forename: 'test', surname: 'nhitwo' }
  ]
});

// All named policies keyed by their policy number, for use with the 'multiple-policies' mode.
// singleModePolicy is included so it appears in the full policy list.
export const mockPolicies: PolicyMap = {
  ...Object.fromEntries(Object.values(namedPolicies).map((p: Policy) => [p.policyNumber, p])),
  [singleModePolicyInstance]: singleModePolicy
} as PolicyMap;

// Dev-only: peril keys for which a server-side mock-answer set exists (see
// `server/data/mockAnswerSets.ts`). Selecting one pre-fills the journey and jumps
// straight to that peril's first FNOL section. Keep in sync with the server registry —
// `mockAnswerSets.ts` has a compile-time check that every key here has a fixture.
export const mockAnswerSetKeys = ['EOW', 'AccidentalDamage'] as const satisfies readonly (keyof PerilType)[];

export type MockPolicyDataMode = 'single-policy' | 'multiple-policies' | 'none';

export const singlePolicyMode: MockPolicyDataMode = 'single-policy';
export const multiplePoliciesMode: MockPolicyDataMode = 'multiple-policies';

export interface MockGetPoliciesResult {
  policies: PolicyMap;
  customerDetails: CustomerDetails;
}

const customerDetails: CustomerDetails = { emailAddress: 'test@mocktest.com' };

export function getMockPolicyData(
  mockPolicyDataMode: MockPolicyDataMode = multiplePoliciesMode,
  policyKey?: MockPolicyKey
): MockGetPoliciesResult {
  if (mockPolicyDataMode === singlePolicyMode) {
    const policy = policyKey ? namedPolicies[policyKey] : mockPolicies[singleModePolicyInstance];

    return { policies: { [policy.policyNumber]: policy }, customerDetails };
  } else if (mockPolicyDataMode === multiplePoliciesMode) {
    return { policies: mockPolicies, customerDetails };
  }

  throw new Error(`"${mockPolicyDataMode}" is not a valid value for mockPolicyDataMode.`);
}
